import { Role } from '../types/role';

const ROLES_STORAGE_KEY = 'drive_manager_roles';

class RoleService {
  private getStoredRoles(): Role[] {
    try {
      const stored = localStorage.getItem(ROLES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading roles from storage:', error);
      return [];
    }
  }

  private saveRoles(roles: Role[]): void {
    try {
      localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
    } catch (error) {
      console.error('Error saving roles to storage:', error);
    }
  }

  async getRoles(): Promise<Role[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));
    return this.getStoredRoles();
  }

  async addRole(name: string, description?: string): Promise<Role> {
    const existingRoles = this.getStoredRoles();
    
    // Check if role already exists
    const existingRole = existingRoles.find(role => 
      role.name.toLowerCase() === name.toLowerCase()
    );
    
    if (existingRole) {
      throw new Error(`Role "${name}" already exists`);
    }

    const newRole: Role = {
      id: `role_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name.trim(),
      description: description?.trim(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0,
      isActive: true
    };

    const updatedRoles = [...existingRoles, newRole];
    this.saveRoles(updatedRoles);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return newRole;
  }

  async updateRole(id: string, updates: Partial<Role>): Promise<Role> {
    const existingRoles = this.getStoredRoles();
    const roleIndex = existingRoles.findIndex(role => role.id === id);
    
    if (roleIndex === -1) {
      throw new Error(`Role with ID ${id} not found`);
    }

    const updatedRole = {
      ...existingRoles[roleIndex],
      ...updates,
      updatedAt: new Date().toISOString()
    };

    existingRoles[roleIndex] = updatedRole;
    this.saveRoles(existingRoles);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return updatedRole;
  }

  async deleteRole(id: string): Promise<void> {
    const existingRoles = this.getStoredRoles();
    const filteredRoles = existingRoles.filter(role => role.id !== id);
    
    if (filteredRoles.length === existingRoles.length) {
      throw new Error(`Role with ID ${id} not found`);
    }

    this.saveRoles(filteredRoles);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  async incrementUsage(roleName: string): Promise<void> {
    const existingRoles = this.getStoredRoles();
    const roleIndex = existingRoles.findIndex(role => 
      role.name.toLowerCase() === roleName.toLowerCase()
    );
    
    if (roleIndex !== -1) {
      existingRoles[roleIndex].usageCount += 1;
      existingRoles[roleIndex].updatedAt = new Date().toISOString();
      this.saveRoles(existingRoles);
    }
  }

  async searchRoles(query: string): Promise<Role[]> {
    const allRoles = this.getStoredRoles();
    const lowercaseQuery = query.toLowerCase();
    
    return allRoles.filter(role => 
      role.name.toLowerCase().includes(lowercaseQuery) ||
      (role.description && role.description.toLowerCase().includes(lowercaseQuery))
    );
  }
}

export const roleService = new RoleService();
